import { useEffect } from 'react';
import type { GsVideo } from '@/lib/types';

/**
 * In-page preview for a Watch-series episode.
 *
 * The Backend `gs_videos` table stores no playable video URL yet, so instead of
 * sending visitors in a loop back to the same page, each card opens this
 * clearly-labeled sample preview right where they are.
 */
export default function VideoPreviewModal({ video, onClose }: { video: GsVideo | null; onClose: () => void }) {
  useEffect(() => {
    if (!video) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [video, onClose]);

  if (!video) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`${video.title} preview`}
    >
      <div className="absolute inset-0 bg-foreground-950/80 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-3xl bg-foreground-950 border border-foreground-800 rounded-lg overflow-hidden gs-rise">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close preview"
          className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full flex items-center justify-center bg-foreground-950/70 text-background-100 hover:text-accent-400 hover:bg-foreground-900 cursor-pointer transition-colors"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>

        <div className="relative w-full aspect-video">
          <img
            src={video.image}
            alt={video.title}
            title={`${video.title} video preview`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-foreground-950/35"></div>
          <span className="absolute inset-0 m-auto w-16 h-16 md:w-20 md:h-20 rounded-full bg-accent-500 text-foreground-950 flex items-center justify-center">
            <i className="ri-play-fill text-3xl md:text-4xl"></i>
          </span>
          <span className="absolute top-4 left-4 font-label font-bold uppercase tracking-widest text-[10px] px-2.5 py-1 rounded-sm bg-accent-500 text-foreground-950">
            Sample preview
          </span>
        </div>

        <div className="p-6 md:p-8">
          <h3 className="font-heading font-bold text-2xl md:text-3xl text-background-50">{video.title}</h3>
          <p className="mt-3 text-foreground-300 text-sm md:text-base">{video.body}</p>
          <p className="mt-5 flex items-start gap-2 text-xs text-foreground-400">
            <span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0"><i className="ri-information-line"></i></span>
            The episode file isn&apos;t attached in this demo — in the live site this card plays the video right here.
          </p>
        </div>
      </div>
    </div>
  );
}